import {
  Component,
  computed,
  inject,
  Input,
  NgZone,
  ViewEncapsulation,
} from '@angular/core';
import { CRMIntegrationsService } from '../../../services/crm-integrations.service';
import { DataSharingService } from '../../../services/data-sharing.service';
import { concatMap, delay, filter, tap, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DashboardBulkSelection, selectionCount, selectionIds, selectionParams } from '../../../types/dashboard-bulk-selection';
import { publicMethods } from '../../../globals';
import { MessageService } from '../../../services/message.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { I18nPluralPipe } from '@angular/common';
import { pluralMappings } from '../../../classes/i18n';

declare var $: any;

@Component({
    selector: 'export-to-crm',
    templateUrl: './export-to-crm.component.html',
    styleUrl: './export-to-crm.component.css',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ExportToCrmComponent {
  @Input() modalId = 'exportToCRMModal';
  @Input() selection: DashboardBulkSelection;
  openedSelection: DashboardBulkSelection;
  readonly selectionCount = selectionCount;
  private readonly closed = new Subject<void>();
  @Input() crmName: string;
  @Input() randomCompanyIds: string[];

  ngZone = inject(NgZone);
  dataStore = inject(DataSharingService);
  crmService = inject(CRMIntegrationsService);
  pubMethods = inject(publicMethods);
  messageService = inject(MessageService);
  snackbar = inject(MatSnackBar);
  pluralPipe = inject(I18nPluralPipe);

  exporterLoading = computed(() => {
    const { isLoadingExporter, isLoadingWizardStepAction } =
      this.crmService.wizardLoading();

    return isLoadingExporter || isLoadingWizardStepAction;
  });

  pluralMappings = pluralMappings;
  MaxSelectedRecords = 1000;

  companyIds: string[] = [];
  searchQueryParams: Record<string, any> = {};
  crmObjectId: number = null;

  foundMatchedList: Record<string, any>[] = [];
  noMatchingList: Record<string, any>[] = [];

  allExistingChecked = false;
  allNewChecked = false;

  newlyCreatedRecordsDict: Record<string, any> = {};
  isCreatingRecords = false;

  ngAfterViewInit(): void {
    $(`#${this.modalId}`).on('show.bs.modal.bulkCRM', e => {
      this.ngZone.run(() => this.getCompanyIdsOrParams());
    });
    $(`#${this.modalId}`).on('hide.bs.modal.bulkCRM', e => {
      this.ngZone.run(() => this.wipeOldDataOnModalClose());
    });
  }

  get formattedCRMName(): string {
    if (this.crmName?.toLowerCase() === 'hubspot') return 'HubSpot';

    return this.crmName;
  }

  getCompanyIdsOrParams(): void {
    this.closed.next();
    this.openedSelection = this.selection;
    this.searchQueryParams = {};
    if (this.openedSelection) {
      if (this.openedSelection.mode === 'none') return;
      this.companyIds = selectionIds(this.openedSelection).map(String);
      const params = selectionParams(this.openedSelection);
      params.keys().forEach(key => this.searchQueryParams[key] = params.get(key));
    } else if (this.pubMethods.hasData(this.randomCompanyIds)) {
      this.companyIds = this.randomCompanyIds;
    } else {
      this.dataStore.currentSelectedCompanyIds.pipe(take(1)).subscribe(companyIds => {
        this.companyIds = companyIds;
      });
    }

    if (!this.openedSelection) {
      this.dataStore.currentSelectedSearchParams.pipe(take(1)).subscribe(searchParams => {
        searchParams.keys().forEach(key => this.searchQueryParams[key] = searchParams.get(key));
      });
    }

    // call search endpoint
    this.getExportSettingsByObjectIdOrParams();
  }

  getExportSettingsByObjectIdOrParams(): void {
    this.crmService
      .getCRMExistingState({
        actionType: 'crm_object_type',
        crmName: this.crmName.toLowerCase(),
      })
      .pipe(
        tap(objectData => {
          if (this.pubMethods.hasData(objectData) && objectData.length) {
            this.crmObjectId = objectData[0].id;
            this.MaxSelectedRecords = objectData[0].maxRecordsPerRequest ?? 1000;
          } else {
            this.messageService.show(
              'Something went wrong, please refresh and try again. If issue persists, please contact us. {crmObjectId}'
            );
          }
        }),
        filter(() => this.pubMethods.hasData(this.crmObjectId)),
        filter(() => {
          const count = this.openedSelection ? selectionCount(this.openedSelection) : this.companyIds.length;
          if (count <= this.MaxSelectedRecords) return true;
          this.messageService.show(`Select no more than ${this.MaxSelectedRecords} companies for this CRM.`);
          return false;
        }),
        concatMap(() => {
          return this.crmService.getCRMExportSettings({
            objectId: this.crmObjectId,
            companyIds: this.companyIds,
            crmName: this.crmName,
            searchQParams: this.searchQueryParams,
          });
        }),
        takeUntil(this.closed),
        tap(data => {
          if (data) {
            this.foundMatchedList = data.matches;
            this.handleInBetweenStateForNoMatching(data.nonMatching);
          }
        })
      )
      .subscribe();
  }

  handleInBetweenStateForNoMatching(
    nonMatchingData: Record<string, any>[]
  ): void {
    this.noMatchingList = nonMatchingData?.filter(record => {
      return !(record.ukCompanyNumber in this.newlyCreatedRecordsDict);
    });
  }

  toggleAllCheckboxes(existingOrNew: 'existing' | 'new') {
    if (existingOrNew === 'existing') {
      this.foundMatchedList.forEach(matchObj => {
        matchObj.checked = this.allExistingChecked;
      });
    }
    if (existingOrNew === 'new') {
      this.noMatchingList?.forEach(obj => {
        obj.checked = this.allNewChecked;
      });
    }
  }

  updateHubspotRecords(): void {
    const selectedCompanies = this.foundMatchedList.filter(obj => obj.checked);
    if (selectedCompanies?.length > this.MaxSelectedRecords) {
      this.messageService.showAndHideAfterDelay(
        `Maximum number of records to update must not exceed ${this.MaxSelectedRecords}!`,
        4000
      );
      return;
    }

    if (!selectedCompanies.length) {
      this.pubMethods.log('Please select one or more records.');
      return;
    }

    const recordIdToCompanyNumberObj = {};
    selectedCompanies.forEach(company => {
      const { crmRecordId, ukCompanyNumber } = company;
      recordIdToCompanyNumberObj[crmRecordId] = ukCompanyNumber;
    });

    const payloadObj = {
      crmRecordIdsToCompanyNumbers: recordIdToCompanyNumberObj,
    };

    this.crmService
      .updateCRMRecords({
        objectId: this.crmObjectId,
        crmName: this.crmName,
        payloadObj,
      })
      .subscribe(response => {
        if (response.ok && this.pubMethods.hasData(response.body)) {
          const { numberOfRecordsUpdated } = response.body;
          const pluralisedRecords = this.pluralPipe.transform(
            numberOfRecordsUpdated,
            pluralMappings.record
          );
          const successMsg = `${numberOfRecordsUpdated} ${pluralisedRecords} updated.`;
          this.snackbar.open(successMsg, 'OK', {
            duration: 3100,
          });
        }
      });
  }

  createHubspotRecords(): void {
    const selectedCompaniesToCreate = this.noMatchingList.filter(
      obj =>
        obj.checked && !(obj.ukCompanyNumber in this.newlyCreatedRecordsDict)
    );

    if (selectedCompaniesToCreate?.length > this.MaxSelectedRecords) {
      this.messageService.showAndHideAfterDelay(
        `Maximum number of records to create must not exceed ${this.MaxSelectedRecords}!`,
        4000
      );
      return;
    }
    if (!selectedCompaniesToCreate.length) {
      this.pubMethods.log('Please select one or more records.');
      return;
    }

    this.isCreatingRecords = true;

    const payloadObj = { companyNumbers: [] };
    selectedCompaniesToCreate.forEach(company => {
      payloadObj.companyNumbers.push(company.ukCompanyNumber);
    });

    this.crmService
      .createNewCRMRecords({
        crmName: this.crmName,
        objectId: this.crmObjectId,
        payloadObj,
      })
      .pipe(
        tap({
          next: (response) => {
            if (response.ok && this.pubMethods.hasData(response.body)) {
              const { created } = response.body;
              const pluralisedRecords = this.pluralPipe.transform(
                created?.length,
                pluralMappings.record
              );
              const successMsg = `${created?.length} ${pluralisedRecords} created.`;
              this.snackbar.open(successMsg, 'OK', { duration: 4000 });

              const createdRecordsMap = created.reduce(
                (acc: any, record: any) => {
                  acc[record.ukCompanyNumber] = record;
                  return acc;
                },
                {}
              );
              this.newlyCreatedRecordsDict = {
                ...this.newlyCreatedRecordsDict,
                ...createdRecordsMap,
              };

              this.noMatchingList = this.noMatchingList.filter(record => {
                return !selectedCompaniesToCreate.some(selected =>
                  selected.ukCompanyNumber === record.ukCompanyNumber
                );
              });
            }
          },
          error: () => {
            this.isCreatingRecords = false;
          },
          complete: () => {
            this.isCreatingRecords = false;
          }
        })
      )
      .subscribe();
  }

  wipeOldDataOnModalClose(): void {
    this.closed.next();
    this.reset();

    if (document.activeElement) {
      (document.activeElement as any).blur();
    }
  }

  getCRMIcon(crmType: string): string {
    const fontAwesomeCRMs = new Set(['hubspot', 'salesforce']);

    if (fontAwesomeCRMs.has(crmType)) {
      return `<i class="fab fa-${crmType}"></i>`;
    }

    const svgPath = `/assets/tech-icons/${crmType}.svg`;
    return `<img src="${svgPath}" alt="${crmType.charAt(0).toUpperCase() + crmType.slice(1)}">`;
  }

  reset(): void {
    this.foundMatchedList = [];
    this.noMatchingList = [];
    this.searchQueryParams = {};
    this.crmObjectId = null;
    this.newlyCreatedRecordsDict = {};
    this.allExistingChecked = false;
    this.allNewChecked = false;
    this.companyIds = [];
    this.isCreatingRecords = false;
  }

  ngOnDestroy(): void {
    this.closed.next(); this.closed.complete();
    $(`#${this.modalId}`).off('.bulkCRM');
  }
}
